import type { ListType } from '@awwyeah/shared';

/**
 * Built-in template library (ROADMAP §2 Phase 1 Lists item 6). Static data owned by this
 * module — no server state, no network. "Customize" pre-checks every item (the design's
 * "un-picking is faster than building"); `instantiateTemplate` below turns a selection
 * into real `ListMeta`/`ListItem` writes.
 */
export interface ListTemplateItem {
  text: string;
  detail?: string;
}

export interface ListTemplateGroup {
  name: string;
  items: ListTemplateItem[];
}

export interface ListTemplate {
  id: string;
  name: string;
  type: ListType;
  emoji: string;
  tagline: string;
  groups: ListTemplateGroup[];
}

export const LIST_TEMPLATES: ListTemplate[] = [
  {
    id: 'vacation-packing',
    name: 'Vacation packing',
    type: 'todo',
    emoji: '🧳',
    tagline: 'A week away, nothing forgotten',
    groups: [
      {
        name: 'Documents & money',
        items: [
          { text: 'IDs / passports' },
          { text: 'Boarding passes / tickets' },
          { text: 'Hotel & rental confirmations' },
          { text: 'Travel insurance card' },
          { text: 'Backup credit card' },
        ],
      },
      {
        name: 'Clothes',
        items: [
          { text: 'Underwear & socks', detail: 'One per day + 2 spare' },
          { text: 'T-shirts / tops' },
          { text: 'Pants / shorts' },
          { text: 'Sleepwear' },
          { text: 'Swimsuit' },
          { text: 'Light jacket' },
          { text: 'Comfortable shoes' },
          { text: 'One nice outfit' },
        ],
      },
      {
        name: 'Toiletries',
        items: [
          { text: 'Toothbrush & toothpaste' },
          { text: 'Deodorant' },
          { text: 'Sunscreen' },
          { text: 'Prescriptions', detail: 'Enough for the whole trip + a few extra days' },
          { text: 'Contacts / glasses' },
          { text: 'Hairbrush' },
        ],
      },
      {
        name: 'Electronics & extras',
        items: [
          { text: 'Phone charger' },
          { text: 'Headphones' },
          { text: 'Camera' },
          { text: 'Portable battery' },
          { text: 'Book / kindle' },
          { text: 'Snacks for the road' },
          { text: 'Reusable water bottle' },
          { text: 'House key for whoever is checking on things' },
        ],
      },
    ],
  },
  {
    id: 'party-prep',
    name: 'Party prep',
    type: 'todo',
    emoji: '🎉',
    tagline: 'Birthdays, cookouts, any excuse',
    groups: [
      {
        name: 'Food',
        items: [
          { text: 'Cake or something like it' },
          { text: 'Main dish' },
          { text: 'Side dish', detail: 'x2' },
          { text: 'Chips & dip' },
          { text: 'Fruit / veggie tray' },
          { text: 'Ice' },
          { text: 'Drinks — non-alcoholic' },
          { text: 'Fancy cheese board' },
        ],
      },
      {
        name: 'Setup',
        items: [
          { text: 'Borrow the folding table' },
          { text: 'Extra chairs' },
          { text: 'Tablecloth' },
          { text: 'Paper plates & napkins' },
          { text: 'Trash bags / extra trash can' },
          { text: 'Decorations / balloons' },
          { text: 'Candles for the cake' },
        ],
      },
      {
        name: 'Before the day',
        items: [
          { text: 'Send invites' },
          { text: 'Confirm the guest count' },
          { text: 'Playlist' },
          { text: 'Clean the living room' },
        ],
      },
    ],
  },
  {
    id: 'camping',
    name: 'Camping',
    type: 'todo',
    emoji: '🏕️',
    tagline: 'Colorado edition — weekend in the mountains',
    groups: [
      {
        name: 'Shelter & sleep',
        items: [
          { text: 'Tent' },
          { text: 'Tent stakes & mallet' },
          { text: 'Sleeping bags' },
          { text: 'Sleeping pads' },
          { text: 'Pillows' },
          { text: 'Extra tarp for the rain' },
        ],
      },
      {
        name: 'Cooking',
        items: [
          { text: 'Camp stove + fuel' },
          { text: 'Lighter / waterproof matches' },
          { text: 'Cooler + ice' },
          { text: 'Cookware & utensils' },
          { text: 'Plates, cups, sporks' },
          { text: 'Dish soap & sponge' },
          { text: 'Trash bags' },
          { text: 'Bear canister', detail: 'Required above 8,000ft in some areas' },
        ],
      },
      {
        name: 'Clothing & gear',
        items: [
          { text: 'Layers — it drops fast after sunset' },
          { text: 'Rain shell' },
          { text: 'Hiking boots' },
          { text: 'Warm hat & gloves' },
          { text: 'Headlamp + spare batteries' },
          { text: 'Camp chairs' },
          { text: 'Daypack' },
          { text: 'Trekking poles' },
        ],
      },
      {
        name: 'Safety & extras',
        items: [
          { text: 'First aid kit' },
          { text: 'Sunscreen & bug spray' },
          { text: 'Paper map', detail: 'No signal past the trailhead' },
          { text: 'Extra water / filter' },
          { text: 'Firewood or permit to gather' },
          { text: 'Portable battery' },
          { text: 'Camera' },
          { text: 'Campground reservation confirmation' },
        ],
      },
    ],
  },
  {
    id: 'weekly-groceries',
    name: 'Weekly staples',
    type: 'shopping',
    emoji: '🛒',
    tagline: 'Your usual — customize and go',
    groups: [
      {
        name: 'Produce',
        items: [
          { text: 'Bananas' },
          { text: 'Spinach' },
          { text: 'Onions' },
          { text: 'Garlic' },
          { text: 'Lemons' },
          { text: 'Avocados' },
        ],
      },
      {
        name: 'Dairy & eggs',
        items: [
          { text: 'Milk' },
          { text: 'Eggs', detail: '1 dozen' },
          { text: 'Butter' },
          { text: 'Yogurt' },
          { text: 'Shredded cheese' },
        ],
      },
      {
        name: 'Meat',
        items: [
          { text: 'Chicken thighs', detail: '2 packs' },
          { text: 'Ground beef' },
          { text: 'Bacon' },
        ],
      },
      {
        name: 'Pantry',
        items: [
          { text: 'Bread' },
          { text: 'Rice' },
          { text: 'Pasta' },
          { text: 'Olive oil' },
          { text: 'Coffee' },
          { text: 'Peanut butter' },
          { text: 'Cereal' },
          { text: 'Paper towels' },
        ],
      },
    ],
  },
];

export function templateItemCount(template: ListTemplate): number {
  return template.groups.reduce((n, group) => n + group.items.length, 0);
}
